import { createReducer, Reducer } from "@reduxjs/toolkit";
import { Pageable } from "../../services/pageable";
import { transformActionWithCountry } from "../../utils/utils";
import { RemoteResource } from "../common/fetchable";
import { Fetchable, EmptyFetchable } from "../common/fetchable/helpers";
import { CryptoActions } from "./actions";
import { CryptoDTO } from "./models/dto";

export namespace Crypto {
  export const allFraudReports: RemoteResource<
    CryptoDTO.Responses.GetAllFraudReport.T,
    CryptoDTO.Responses.GetAllFraudReport.T,
    Pageable.T<keyof CryptoDTO.Responses.GetSingleFraudReport.T>
  > = {
    fetch: CryptoActions.setAllFraudReportPagination,
    updateStore: CryptoActions.setAllFraudReport,
    selector: (store) => store.crypto.allFraudReport.data,
  };

  export const fraudReportSummary: RemoteResource<
    CryptoDTO.Responses.GetFraudReportSummary.T,
    CryptoDTO.Responses.GetFraudReportSummary.T
  > = {
    fetch: CryptoActions.getFraudReportSummary,
    updateStore: CryptoActions.setFraudReportSummary,
    selector: (store) => store.crypto.fraudReportSummary,
  };
}

/**
 * Interface used for Redux Store
 */
export interface Crypto {
  allFraudReport: {
    data: Fetchable<CryptoDTO.Responses.GetAllFraudReport.T>;
    pagination: Pageable.T<keyof CryptoDTO.Responses.GetSingleFraudReport.T>;
  };
  singleFraudReportMap: Record<
    string,
    CryptoDTO.Responses.GetSingleFraudReport.T | "LOADING"
  >;
  newReportForm: Partial<CryptoDTO.Requests.CreateFraudReport.T>;
  fraudReportSummary: Fetchable<CryptoDTO.Responses.GetFraudReportSummary.T>;
}

const initialState: Crypto = {
  allFraudReport: {
    data: EmptyFetchable,
    pagination: {},
  },
  singleFraudReportMap: {},
  newReportForm: {},
  fraudReportSummary: EmptyFetchable,
};

// Reducer
export const cryptoReducer: Reducer<
  Crypto,
  CryptoActions.Actions
> = createReducer(initialState, (builder) => {
  builder
    .addCase(CryptoActions.getAllFraudReport, (state) => {
      state.allFraudReport.data = {
        status: "loading",
      };
    })
    .addCase(CryptoActions.setAllFraudReportPagination, (state, action) => {
      state.allFraudReport.pagination = action.payload;
      state.allFraudReport.data = {
        status: "loading",
      };
    })
    .addCase(CryptoActions.setAllFraudReport, (state, action) => {
      const newPayload = transformActionWithCountry(action.payload);
      state.allFraudReport.data = newPayload;
      if (newPayload.status === "ready") {
        newPayload.data.content.forEach((p) => {
          state.singleFraudReportMap[p.id] = p;
        });
      }
    })
    .addCase(CryptoActions.resetAllFraudReport, (state) => {
      state.allFraudReport.data = EmptyFetchable;
      state.allFraudReport.pagination = {};
    })
    .addCase(CryptoActions.setSingleFraudReport, (state, action) => {
      state.singleFraudReportMap[action.payload.id] = action.payload.result;
    })
    .addCase(CryptoActions.setNewFormReportSection, (state, action) => {
      state.newReportForm = { ...state.newReportForm, ...action.payload };
    })
    .addCase(CryptoActions.resetFraudReportSummary, (state) => {
      state.fraudReportSummary = EmptyFetchable;
    })
    .addCase(CryptoActions.getFraudReportSummary, (state) => {
      state.fraudReportSummary = {
        status: "loading",
      };
    })
    .addCase(CryptoActions.setFraudReportSummary, (state, action) => {
      state.fraudReportSummary = action.payload;
    });
});
